/**
 * ORLAVEN — Announcement Bar (sections/custom.liquid)
 *
 * PERF: Previously ~7.9 KB of inline <script> rendered into the HTML on every
 * page load. That blocked the parser, could not be cached, and was the single
 * largest contributor to the 1971 ms of inline-script bootup time Lighthouse
 * attributed to `orlaven.com/`. Now external, cached and deferred.
 *
 * PERF: Zero forced reflow. The old version read `.offsetHeight` and then wrote
 * `style.transform` in the same task (and again on every resize), causing
 * forced synchronous layout — Lighthouse measured 52.5 ms. Slide translation is
 * now driven purely by CSS custom properties (`--sab-i`), which are
 * compositor-only. Height is observed with ResizeObserver + rAF instead of being
 * read on demand.
 *
 * NOTE: named orlaven-* to avoid colliding with Shopify's own
 * assets/announcement-bar.js (used by sections/header-announcements.liquid).
 */

const parseEndDate = (value) => {
  if (!value) return null;
  const [datePart, timePart] = value.trim().split(' ');
  const [year, month, day] = (datePart || '').split('-').map((n) => parseInt(n, 10));
  if (!year || !month || !day) return null;
  let hours = 0;
  let minutes = 0;
  if (timePart) {
    const [h, m] = timePart.split(':');
    hours = parseInt(h, 10) || 0;
    minutes = parseInt(m, 10) || 0;
  }
  return new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
};

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

function initAnnouncementBar(container) {
  if (!container || container.dataset.sabReady === 'true') return;
  container.dataset.sabReady = 'true';

  const sectionId = container.dataset.sectionId;
  const displayMode = container.dataset.displayMode;
  const dismissedKey = container.dataset.dismissedKey;
  const autoRotate = container.dataset.autoRotate === 'true';
  const rotateSpeed = (parseInt(container.dataset.rotateSpeed, 10) || 5) * 1000;
  const countdownEnable = container.dataset.countdownEnable === 'true';
  const countdownEnd = container.dataset.countdownEnd;
  const countdownExpiredMsg = container.dataset.countdownExpiredMsg;

  // Session dismiss — bail out before doing any further work.
  try {
    if (sessionStorage.getItem(dismissedKey) === '1') {
      container.classList.add('sab-hidden');
      return;
    }
  } catch (_) {
    /* sessionStorage blocked (private mode) — continue normally */
  }

  let currentIndex = 0;
  let timer = null;
  let countdownInterval = null;

  let slides = [];
  let slider = null;

  if (displayMode === 'slider') {
    slider = container.querySelector('[data-slider-container]');
    slides = Array.from(container.querySelectorAll('.sab-slide'));
  } else if (displayMode === 'fade') {
    slides = Array.from(container.querySelectorAll('.sab-fade__slide'));
  }

  const totalSlides = slides.length;
  const dots = Array.from(container.querySelectorAll('.sab-dot'));
  const prevBtn = container.querySelector('.sab-arrow--prev');
  const nextBtn = container.querySelector('.sab-arrow--next');
  const closeBtn = container.querySelector('.sab-close');
  const content = container.querySelector('.sab-content');

  const updateDots = () => {
    if (!dots.length) return;
    dots.forEach((dot, i) => dot.classList.toggle('sab-dot--active', i === currentIndex));
  };

  const goTo = (index) => {
    if (totalSlides <= 0) return;
    if (index < 0) index = totalSlides - 1;
    if (index >= totalSlides) index = 0;
    currentIndex = index;

    if (displayMode === 'slider' && slider) {
      // PERF: compositor-only write. No offsetHeight read => no forced reflow.
      container.style.setProperty('--sab-i', String(currentIndex));
    } else if (displayMode === 'fade') {
      slides.forEach((slide, i) =>
        slide.classList.toggle('sab-fade__slide--active', i === currentIndex)
      );
    }
    updateDots();
  };

  const stopTimer = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const startTimer = () => {
    if (!autoRotate || totalSlides <= 1 || displayMode === 'marquee') return;
    stopTimer();
    timer = setInterval(() => goTo(currentIndex + 1), rotateSpeed);
  };

  const resetTimer = () => {
    stopTimer();
    startTimer();
  };

  // PERF: measure the height off the critical path, and only when the box
  // actually changes size. The write is deferred to the next frame so we never
  // read-then-write layout inside the same task.
  if (displayMode === 'slider' && content && 'ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect?.height;
      if (!height) return;
      requestAnimationFrame(() => {
        container.style.setProperty('--sab-h', `${height}px`);
      });
    });
    resizeObserver.observe(content);
  }

  prevBtn?.addEventListener('click', () => {
    goTo(currentIndex - 1);
    resetTimer();
  });

  nextBtn?.addEventListener('click', () => {
    goTo(currentIndex + 1);
    resetTimer();
  });

  dots.forEach((dot, idx) =>
    dot.addEventListener('click', () => {
      goTo(idx);
      resetTimer();
    })
  );

  closeBtn?.addEventListener('click', () => {
    container.classList.add('sab-hidden');
    try {
      sessionStorage.setItem(dismissedKey, '1');
    } catch (_) {
      /* ignore */
    }
    stopTimer();
    if (countdownInterval) clearInterval(countdownInterval);
  });

  container.addEventListener('mouseenter', stopTimer);
  container.addEventListener('mouseleave', startTimer);

  container.addEventListener('keydown', (event) => {
    if (displayMode === 'marquee') return;
    if (event.key === 'ArrowLeft') {
      goTo(currentIndex - 1);
      resetTimer();
    } else if (event.key === 'ArrowRight') {
      goTo(currentIndex + 1);
      resetTimer();
    }
  });

  // PERF: don't burn timers/paints while the tab is in the background.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopTimer();
    else startTimer();
  });

  if (countdownEnable && countdownEnd) {
    const timerEl = container.querySelector('[data-countdown-timer]');
    const endDate = parseEndDate(countdownEnd);

    if (timerEl && endDate) {
      const updateCountdown = () => {
        const diff = endDate - new Date();
        if (diff <= 0) {
          timerEl.textContent = countdownExpiredMsg || 'Offer has expired';
          if (countdownInterval) clearInterval(countdownInterval);
          return;
        }
        const days = Math.floor(diff / 86400000);
        const hrs = Math.floor((diff % 86400000) / 3600000);
        const mins = Math.floor((diff % 3600000) / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        timerEl.textContent = `${pad(days)}:${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
      };
      updateCountdown();
      countdownInterval = setInterval(updateCountdown, 1000);
    }
  }

  startTimer();

  // Theme editor support — only wired up inside the editor.
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:block:select', (event) => {
      if (event.detail.sectionId !== sectionId) return;
      stopTimer();
      const blockEl = event.target.closest('[data-slide-index]');
      if (blockEl) goTo(parseInt(blockEl.getAttribute('data-slide-index'), 10) || 0);
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      if (event.detail.sectionId === sectionId) startTimer();
    });
  }
}

const initAll = () => {
  document.querySelectorAll('[data-sab-root]').forEach(initAnnouncementBar);
};

initAll();

// Re-init after a section is re-rendered in the theme editor.
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', initAll);
}
